
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Users, AlertTriangle, Circle, Ban, PauseCircle, Trash2, Layers } from 'lucide-react';
import { NodeData } from './types';
import { useEditorContext } from './EditorContext';

const GroupNode: React.FC<NodeProps<NodeData>> = ({ id, data, selected }) => {
  const { iconMap, onUpdateNode } = useEditorContext();
  const IconComponent = iconMap[data.iconPath] || iconMap['Layout'];

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(data.name);
  const inputRef = useRef<HTMLInputElement>(null);

  // Status priority: Cycle Error > Deleted > Suspended > Inactive > Root/Active
  const getStatusStyles = () => {
    if (data.inCycle) {
      return {
        border: 'border-red-400',
        bg: 'bg-red-50',
        iconBg: 'bg-red-100 text-red-600',
        badge: 'bg-red-600 text-white',
        badgeText: 'CYCLE ERROR',
        opacity: 'opacity-100'
      };
    }
    if (data.isDeleted) {
      return {
        border: 'border-slate-200',
        bg: 'bg-slate-100',
        iconBg: 'bg-slate-200 text-slate-400',
        badge: 'bg-slate-500 text-white',
        badgeText: 'DELETED',
        opacity: 'opacity-75 grayscale'
      };
    }
    if (data.isSuspended) {
      return {
        border: 'border-amber-300',
        bg: 'bg-amber-50',
        iconBg: 'bg-amber-100 text-amber-600',
        badge: 'bg-amber-500 text-white',
        badgeText: 'SUSPENDED',
        opacity: 'opacity-100'
      };
    }
    if (!data.isActive) {
      return {
        border: 'border-slate-200',
        bg: 'bg-slate-50',
        iconBg: 'bg-slate-200 text-slate-500',
        badge: 'bg-slate-200 text-slate-600',
        badgeText: 'INACTIVE',
        opacity: 'opacity-90'
      };
    }
    // Default Active / Root
    return {
      border: selected ? 'border-teal-500' : 'border-transparent',
      bg: data.isRootNode ? 'bg-slate-50' : 'bg-white',
      iconBg: data.isRootNode ? 'bg-teal-100 text-teal-600' : 'bg-slate-100 text-slate-500',
      badge: data.isRootNode ? 'bg-teal-600 text-white' : null,
      badgeText: 'ROOT GROUP',
      opacity: 'opacity-100'
    };
  };

  const styles = getStatusStyles();

  // --- Editing Logic ---

  const startEditing = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsEditing(true);
    setEditName(data.name);
  }, [data.name]);

  const stopEditing = useCallback(() => {
    setIsEditing(false);
    if (editName !== data.name && onUpdateNode) {
      onUpdateNode(id, { name: editName });
    }
  }, [editName, data.name, id, onUpdateNode]);

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation(); // Stop React Flow from handling backspace/delete
    if (e.key === 'Enter') {
      stopEditing();
    }
  };

  // Keyboard shortcut Ctrl+R
  useEffect(() => {
    if (!selected || isEditing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        startEditing();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selected, isEditing, startEditing]);

  // Focus and select text
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Update local state if prop changes while not editing
  useEffect(() => {
      if (!isEditing) {
          setEditName(data.name);
      }
  }, [data.name, isEditing]);

  return (
    <div 
      className={`
        relative px-4 py-3 rounded-xl shadow-lg border-2 transition-all duration-300
        ${styles.border}
        ${styles.bg}
        ${styles.opacity}
        ${selected ? 'ring-4 ring-teal-500/20 scale-105 z-50' : 'hover:border-slate-300'}
        min-w-[220px] group font-sans
      `}
      onDoubleClick={startEditing}
    >
      {/* 1. Status Badge */}
      {styles.badgeText && (styles.badge || data.inCycle || data.isDeleted || data.isSuspended || !data.isActive) && (
        <div className={`
          absolute -top-3 left-4 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest shadow-sm flex items-center gap-1 z-10
          ${styles.badge || 'bg-slate-200 text-slate-600'}
          ${data.inCycle ? 'animate-pulse' : ''}
        `}>
          {data.isDeleted && <Trash2 size={8} />}
          {data.isSuspended && <PauseCircle size={8} />}
          {styles.badgeText}
        </div>
      )}

      {/* 2. Hero Section */}
      <div className="flex items-center gap-3">
        <div className={`
          w-10 h-10 rounded-xl flex items-center justify-center transition-colors shrink-0
          ${styles.iconBg}
        `}>
          {IconComponent && <IconComponent size={20} strokeWidth={2.5} />}
        </div>
        <div className="flex-1 overflow-hidden min-h-[36px] flex flex-col justify-center">
          {isEditing ? (
             <input
               ref={inputRef}
               type="text"
               value={editName}
               onChange={(e) => setEditName(e.target.value)}
               onBlur={stopEditing}
               onKeyDown={handleInputKeyDown}
               className="w-full text-sm font-bold text-slate-800 border border-teal-500 rounded px-1 py-0.5 outline-none bg-white shadow-sm nodrag"
               onClick={(e) => e.stopPropagation()}
               onMouseDown={(e) => e.stopPropagation()}
             />
          ) : (
            <h3 className={`text-sm font-bold text-slate-800 truncate leading-tight ${data.isDeleted ? 'line-through decoration-slate-400' : ''}`}>
              {data.name || 'Unnamed Group'}
            </h3>
          )}
          
          <p className="text-[10px] text-slate-500 truncate mt-0.5 opacity-80 italic">
            {data.description || 'No description provided...'}
          </p>
        </div>
      </div>

      {/* 3. Stats Footer */}
      <div className="mt-3 pt-2 border-t border-slate-200/50 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 bg-white/50 px-2 py-1 rounded-md border border-slate-100 text-[10px] font-mono">
          <div className="flex items-center gap-1 text-slate-400" title="Direct Members">
            <Users size={12} strokeWidth={2} />
            <span>{data.memberCount || 0}</span>
          </div>
          <div className="w-px h-3 bg-slate-300 mx-0.5" />
          <div className="flex items-center gap-1 text-teal-600" title="Inclusive Members">
            <Layers size={12} strokeWidth={2} />
            <span>{data.inclusiveMemberCount || data.memberCount || 0}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-1 text-[9px] font-bold uppercase">
          {data.inCycle ? (
            <div className="text-red-600 flex items-center gap-1">
              <AlertTriangle size={10} />
              ERROR
            </div>
          ) : data.isActive && !data.isDeleted && !data.isSuspended ? (
            <div className="flex items-center gap-1 text-emerald-600">
              <Circle size={8} className="fill-emerald-500" />
              Active
            </div>
          ) : (
            <span className="text-slate-400">Inactive</span>
          )}
        </div>
      </div>

      {/* 4. Handles */}
      {!data.isRootNode && (
        <Handle
          type="target"
          position={Position.Left}
          className="!w-3 !h-3 !bg-slate-400 !border-2 !border-white hover:!bg-teal-500 transition-colors"
        />
      )}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !bg-slate-400 !border-2 !border-white hover:!bg-teal-500 transition-colors"
      />
    </div>
  );
};

// Memoized: with large graphs (1000+ nodes) every state change in the editor
// would otherwise re-render all node components.
export default React.memo(GroupNode);
